import { useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Columns3,
  FileCode2,
  Fingerprint,
  KeyRound,
  Link2,
  Search,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { Badge, Input, Select } from "@/components/ui";
import type { TableCard } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { CodeBlock, CopyButton, formatBytes, formatDateTime, InlineText, KindBadge, QualityIndicator, SectionTitle } from "./shared";

// ---------------------------------------------------------------------------
// Generic recursive value rendering: nothing in a card is ever dropped.
// ---------------------------------------------------------------------------

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isPrimitive = (v: unknown) => v === null || v === undefined || ["string", "number", "boolean"].includes(typeof v);
const looksLikeSql = (s: string) => /^\s*(select|with|insert|update|delete|merge|create)\b/i.test(s) && s.length > 20;
const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && !!x.trim()) : typeof v === "string" && v.trim() ? [v] : []);
const isEmpty = (v: unknown) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) || (isObj(v) && Object.values(v).every((x) => x === null || x === undefined || x === "" || (Array.isArray(x) && !x.length)));

/** "datawarehouse-dev-sl.dataset.table" → "dataset.table" (the fqn form the catalog uses). */
export const shortFqn = (s: string) => {
  const parts = s.replace(/`/g, "").split(".");
  return parts.length >= 3 ? parts.slice(-2).join(".") : s;
};

const KEY_LABELS: Record<string, string> = {
  description: "Beschrijving",
  meaning: "Betekenis",
  usage: "Gebruik",
  unit: "Eenheid",
  additivity: "Additiviteit",
  mode: "Modus",
  type: "Type",
  role: "Rol",
  synonyms: "Synoniemen",
  caveats: "Valkuilen",
  confidence: "Betrouwbaarheid",
  sources: "Bronnen",
  model: "Model",
  source: "Bron",
  text: "Tekst",
  status: "Status",
  rows: "Rijen",
  bytes: "Bytes",
  columns: "Kolommen",
  sample: "Voorbeeld",
  jobs: "Queries",
  users: "Gebruikers",
  rows_per_key: "Rijen per sleutel",
  unique: "Uniek",
  distinct: "Verschillend",
  path: "Pad",
  claim: "Bewering",
  check: "Controle",
  passed: "Geslaagd",
  result: "Resultaat",
};

export function humanizeKey(k: string): string {
  if (KEY_LABELS[k]) return KEY_LABELS[k];
  const s = k.replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function PrimitiveView({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") return <span className="text-muted-foreground">–</span>;
  if (typeof value === "boolean") return <span>{value ? "ja" : "nee"}</span>;
  if (typeof value === "number") return <span className="tabular-nums">{value.toLocaleString("nl-NL")}</span>;
  const s = String(value);
  if (looksLikeSql(s)) return <CodeBlock code={s} />;
  return <InlineText text={s} />;
}

/** Table for an array of objects: one column per key (preferred keys first). */
export function ObjectTable({ rows, preferred = [] }: { rows: Obj[]; preferred?: string[]; compact?: boolean }) {
  const keys = useMemo(() => {
    const seen = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => seen.add(k)));
    const first = preferred.filter((k) => seen.has(k));
    return [...first, ...[...seen].filter((k) => !first.includes(k))];
  }, [rows, preferred]);
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-sl-grey-50 text-left text-xs font-semibold text-muted-foreground">
          <tr>
            {keys.map((k) => (
              <th key={k} className="border-b border-border px-3 py-2 font-semibold">
                {humanizeKey(k)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              {keys.map((k, ci) => (
                <td key={k} className={cn("max-w-md px-3 py-2 align-top", ci === 0 && "font-mono text-xs font-medium")}>
                  <ValueView value={r[k]} depth={2} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ValueView({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (isPrimitive(value)) return <PrimitiveView value={value} />;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground">(leeg)</span>;
    if (value.every((v) => typeof v === "string" || typeof v === "number") && value.every((v) => String(v).length <= 60)) {
      return <Chips items={value.map(String)} />;
    }
    if (value.every(isPrimitive)) {
      return (
        <ul className="list-disc space-y-1 pl-5">
          {value.map((v, i) => (
            <li key={i}>
              <PrimitiveView value={v} />
            </li>
          ))}
        </ul>
      );
    }
    if (value.every(isObj) && depth < 3) {
      const nKeys = new Set(value.flatMap((v) => Object.keys(v as Obj))).size;
      if (nKeys <= 8) return <ObjectTable rows={value as Obj[]} />;
    }
    return (
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className="rounded-md border border-border p-2.5">
            <ValueView value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  if (isObj(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className="text-muted-foreground">(leeg)</span>;
    if (depth > 5) return <code className="whitespace-pre-wrap break-all font-mono text-xs">{JSON.stringify(value)}</code>;
    return (
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[minmax(8rem,auto)_1fr]">
        {entries.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-xs font-medium text-muted-foreground sm:pt-0.5">{humanizeKey(k)}</dt>
            <dd className="min-w-0">
              <ValueView value={v} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span>{String(value)}</span>;
}

function Chips({ items, mono }: { items: string[]; mono?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((v, i) => (
        <span key={i} className={cn("rounded-md border border-border bg-sl-grey-50 px-2 py-0.5 text-[13px]", mono && "font-mono text-xs")}>
          {v}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function Block({ title, icon, children, className, aside }: { title: ReactNode; icon?: ReactNode; children: ReactNode; className?: string; aside?: ReactNode }) {
  return (
    <section className={cn("min-w-0", className)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <SectionTitle icon={icon} className="mb-0">
          {title}
        </SectionTitle>
        {aside}
      </div>
      <div className="text-sm leading-relaxed">{children}</div>
    </section>
  );
}

function Prose({ value }: { value: unknown }) {
  if (typeof value === "string") return <InlineText text={value} className="block max-w-[75ch] text-[15px] leading-relaxed" />;
  return <ValueView value={value} />;
}

/** Caveat/pitfall item: `{text, source}` or a plain string. */
function caveatText(v: unknown): { text: string; source?: string; rest?: Obj } {
  if (typeof v === "string") return { text: v };
  if (isObj(v)) {
    const { text, description, source, ...rest } = v;
    return { text: str(text) ?? str(description) ?? JSON.stringify(v), source: str(source), rest: Object.keys(rest).length ? rest : undefined };
  }
  return { text: String(v) };
}

const SOURCE_LABEL: Record<string, string> = { llm: "AI-afgeleid", human: "Handmatig", curated: "Gecureerd", schema: "Schema", description: "dbt-beschrijving" };

function SourceTag({ source }: { source?: string }) {
  if (!source) return null;
  return <span className="ml-2 whitespace-nowrap text-xs text-muted-foreground">· {SOURCE_LABEL[source] ?? source}</span>;
}

function CaveatList({ items }: { items: unknown[] }) {
  return (
    <ul className="space-y-2">
      {items.map((p, i) => {
        const c = caveatText(p);
        return (
          <li key={i} className="flex gap-2.5 rounded-md border border-sl-yellow/60 bg-sl-yellow-soft/60 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sl-yellow-dark" aria-hidden />
            <div className="min-w-0 flex-1 text-sm leading-relaxed">
              <InlineText text={c.text} />
              <SourceTag source={c.source} />
              {c.rest && (
                <div className="mt-1.5">
                  <ValueView value={c.rest} depth={1} />
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TableLink({ fqn, onOpen, children }: { fqn: string; onOpen?: (fqn: string) => void; children?: ReactNode }) {
  if (!onOpen) return <span className="font-mono text-[13px]">{children ?? fqn}</span>;
  return (
    <button
      type="button"
      onClick={() => onOpen(shortFqn(fqn))}
      className="inline-flex max-w-full items-center gap-1 rounded text-left font-mono text-[13px] font-medium text-info underline-offset-2 hover:underline"
      title={`Open ${shortFqn(fqn)}`}
    >
      <span className="min-w-0 break-all">{children ?? shortFqn(fqn)}</span>
    </button>
  );
}

function Fact({ label, children, title }: { label: string; children: ReactNode; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

function formatCount(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} mld`;
  if (n >= 1e6) return `${(n / 1e6).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} mln`;
  if (n >= 1e4) return `${(n / 1e3).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}k`;
  return n.toLocaleString("nl-NL");
}

const formatDate = (iso?: string) => {
  if (!iso) return "–";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
};

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const ROLE_LABEL: Record<string, string> = {
  primary_key: "Primaire sleutel",
  foreign_key: "Sleutel (FK)",
  key: "Sleutel",
  measure: "Maat",
  metric: "Maat",
  dimension: "Attribuut",
  attribute: "Attribuut",
  date: "Datum",
  timestamp: "Tijdstip",
  flag: "Vlag",
  technical: "Technisch",
  identifier: "Identificatie",
  natural_key: "Natuurlijke sleutel",
};
const roleLabel = (r?: string) => (r ? (ROLE_LABEL[r] ?? humanizeKey(r)) : "–");
const colName = (c: Obj) => str(c.name) ?? str(c.column) ?? "?";
const colType = (c: Obj) => str(c.type) ?? str(c.data_type);
const colJobs = (c: Obj) => (isObj(c.observed_usage) ? num(c.observed_usage.jobs) : undefined);
const COL_CONSUMED = new Set([
  "name",
  "column",
  "type",
  "data_type",
  "mode",
  "description",
  "role",
  "meaning",
  "unit",
  "additivity",
  "usage",
  "caveats",
  "synonyms",
  "observed_usage",
  "joins_to",
  "personal_data",
  "nested_fields",
  "is_partition",
  "confidence",
  "sources",
  "model",
  "derived_at_build_time",
]);

type ColSort = "position" | "name" | "usage";

function ColumnDetail({ c, onOpenTable, onNext }: { c: Obj; onOpenTable?: (fqn: string) => void; onNext?: () => void }) {
  const caveats = Array.isArray(c.caveats) ? c.caveats : c.caveats ? [c.caveats] : [];
  const syn = strList(c.synonyms);
  const usage = isObj(c.observed_usage) ? c.observed_usage : null;
  const rest = Object.fromEntries(Object.entries(c).filter(([k, v]) => !COL_CONSUMED.has(k) && !isEmpty(v)));
  const rows: [string, ReactNode][] = [];
  if (str(c.description) && str(c.meaning)) rows.push(["Beschrijving (dbt)", <InlineText text={str(c.description)!} />]);
  if (str(c.usage)) rows.push(["Gebruik", <InlineText text={str(c.usage)!} />]);
  if (str(c.unit) && c.unit !== "none") rows.push(["Eenheid", c.unit as string]);
  if (str(c.additivity) && c.additivity !== "none") rows.push(["Additiviteit", c.additivity as string]);
  if (str(c.mode)) rows.push(["Modus", c.mode as string]);
  if (syn.length) rows.push(["Synoniemen", <Chips items={syn} />]);
  const joinsTo = strList(c.joins_to);
  if (joinsTo.length)
    rows.push([
      "Joint naar",
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {joinsTo.map((t) => (
          <TableLink key={t} fqn={t} onOpen={onOpenTable} />
        ))}
      </div>,
    ]);
  if (usage) {
    const roles = Object.entries(usage).filter(([k, v]) => k !== "jobs" && typeof v === "number") as [string, number][];
    rows.push([
      "Gebruik in queries",
      <span className="tabular-nums">
        {num(usage.jobs)?.toLocaleString("nl-NL") ?? "–"} queries
        {roles.length > 0 && <span className="text-muted-foreground"> · {roles.map(([k, v]) => `${USAGE_ROLE[k] ?? k} ${v}`).join(" · ")}</span>}
      </span>,
    ]);
    if (isObj(usage.aggregations)) rows.push(["Aggregaties", <ValueView value={usage.aggregations} depth={2} />]);
  }
  const prov = [
    num(c.confidence) !== undefined && `geschatte zekerheid ${Math.round(num(c.confidence)! * 100)}% (door het bouwproces, niet getest)`,
    isObj(c.sources) &&
      Object.entries(c.sources)
        .map(([k, v]) => `${humanizeKey(k).toLowerCase()}: ${SOURCE_LABEL[String(v)] ?? v}`)
        .join(" · "),
    str(c.model) && `model ${c.model}`,
  ].filter(Boolean) as string[];
  return (
    <div className="space-y-3 border-t border-dashed border-border bg-sl-grey-50/70 px-4 py-3 md:pl-6">
      <div className="flex items-center justify-between text-[13px] font-semibold text-muted-foreground">
        <span>
          Details van <span className="font-mono text-foreground">{colName(c)}</span>
        </span>
        {onNext && (
          <button type="button" onClick={onNext} className="inline-flex items-center gap-1 font-medium text-info hover:underline">
            Volgende kolom
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {caveats.length > 0 && <CaveatList items={caveats} />}
      {rows.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[9rem_1fr] xl:grid-cols-[9rem_1fr_9rem_1fr]">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-xs font-medium text-muted-foreground sm:pt-0.5">{k}</dt>
              <dd className="min-w-0 leading-relaxed">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {Array.isArray(c.nested_fields) && c.nested_fields.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Geneste velden ({c.nested_fields.length})</div>
          <ObjectTable rows={c.nested_fields.filter(isObj)} preferred={["path", "type", "description"]} />
        </div>
      )}
      {Object.keys(rest).length > 0 && <ValueView value={rest} depth={1} />}
      {prov.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none font-medium hover:text-foreground">Herkomst van deze beschrijving</summary>
          <p className="mt-1 leading-relaxed">{prov.join(" · ")}</p>
        </details>
      )}
    </div>
  );
}

const USAGE_ROLE: Record<string, string> = { select: "select", filter: "filter", aggregate: "aggregatie", join: "join", group: "group by", order: "order by", other: "overig" };

function ColumnsTable({ columns, pk, partition, onOpenTable }: { columns: Obj[]; pk: string[]; partition: string[]; onOpenTable?: (fqn: string) => void }) {
  const [filter, setFilter] = useState("");
  const [role, setRole] = useState("");
  const [sort, setSort] = useState<ColSort>("position");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const roles = useMemo(() => {
    const m = new Map<string, number>();
    columns.forEach((c) => {
      const r = str(c.role);
      if (r) m.set(r, (m.get(r) ?? 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [columns]);

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    let list = columns.map((c, i) => ({ c, i }));
    if (role) list = list.filter(({ c }) => c.role === role);
    if (f)
      list = list.filter(({ c }) =>
        [colName(c), str(c.meaning), str(c.description), str(c.usage), ...strList(c.synonyms)].filter(Boolean).join(" ").toLowerCase().includes(f),
      );
    if (sort === "name") list.sort((a, b) => colName(a.c).localeCompare(colName(b.c)));
    if (sort === "usage") list.sort((a, b) => (colJobs(b.c) ?? -1) - (colJobs(a.c) ?? -1));
    return list;
  }, [columns, filter, role, sort]);

  const toggle = (k: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 pb-3">
        <div className="relative w-full sm:w-auto sm:min-w-[16rem] sm:flex-1 lg:max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Zoek op naam, betekenis of synoniem…" className="h-9 pl-8" aria-label="Kolommen filteren" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 w-auto" aria-label="Filter op rol">
            <option value="">Alle rollen ({columns.length})</option>
            {roles.map(([r, n]) => (
              <option key={r} value={r}>
                {roleLabel(r)} ({n})
              </option>
            ))}
          </Select>
          <Select value={sort} onChange={(e) => setSort(e.target.value as ColSort)} className="h-9 w-auto" aria-label="Sortering">
            <option value="position">Volgorde in tabel</option>
            <option value="name">Naam (A–Z)</option>
            <option value="usage">Meest gebruikt</option>
          </Select>
        </div>
        <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground sm:ml-auto" aria-live="polite">
          {shown.length === columns.length ? `${columns.length} kolommen` : `${shown.length} van ${columns.length} kolommen`}
        </span>
      </div>

      <div className="rounded-md border border-border" role="table" aria-label="Kolommen">
        <div
          role="row"
          className="sticky top-11 z-[5] hidden grid-cols-[minmax(12rem,1.1fr)_6.5rem_8rem_minmax(0,3fr)_7rem_1.25rem] gap-3 rounded-t-md border-b border-border bg-sl-grey-50 px-4 py-2 text-xs font-semibold text-muted-foreground md:grid"
        >
          <span role="columnheader">Kolom</span>
          <span role="columnheader">Type</span>
          <span role="columnheader">Rol</span>
          <span role="columnheader">Betekenis</span>
          <span role="columnheader" className="text-right">
            Queries (180 d)
          </span>
          <span />
        </div>
        {shown.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Geen kolommen voor dit filter.{" "}
            <button
              type="button"
              className="font-medium text-foreground underline underline-offset-2"
              onClick={() => {
                setFilter("");
                setRole("");
              }}
            >
              Filter wissen
            </button>
          </div>
        ) : (
          shown.map(({ c, i }, idx) => {
            const name = colName(c);
            const k = `${i}:${name}`;
            const isOpen = open.has(k);
            const jobs = colJobs(c);
            const meaning = str(c.meaning) ?? str(c.description);
            const nCaveats = Array.isArray(c.caveats) ? c.caveats.length : 0;
            const isPk = pk.includes(name);
            const isPart = partition.includes(name) || c.is_partition === true;
            return (
              <div key={k} id={`kol-${k}`} role="row" className="scroll-mt-24 border-b border-border last:border-0">
                <button
                  type="button"
                  onClick={() => toggle(k)}
                  aria-expanded={isOpen}
                  className={cn(
                    "relative flex w-full flex-col gap-1 px-4 py-2.5 pr-10 text-left transition-colors hover:bg-sl-grey-50 md:grid md:grid-cols-[minmax(12rem,1.1fr)_6.5rem_8rem_minmax(0,3fr)_7rem_1.25rem] md:gap-3 md:pr-4 md:items-start",
                    isOpen && "bg-sl-grey-50",
                  )}
                >
                  <span role="cell" className="min-w-0">
                    <span className="block break-all font-mono text-[13px] font-semibold text-foreground">{name}</span>
                    {(isPk || isPart || c.personal_data || nCaveats > 0) && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {isPk && (
                          <Badge tone="dark" className="px-1.5 py-0 text-[11px]">
                            <KeyRound className="h-3 w-3" />
                            PK
                          </Badge>
                        )}
                        {isPart && (
                          <Badge tone="info" className="px-1.5 py-0 text-[11px]">
                            Partitie
                          </Badge>
                        )}
                        {!!c.personal_data && (
                          <Badge tone="danger" className="px-1.5 py-0 text-[11px]">
                            <ShieldAlert className="h-3 w-3" />
                            Persoonsgegevens
                          </Badge>
                        )}
                        {nCaveats > 0 && (
                          <Badge tone="warning" className="px-1.5 py-0 text-[11px]">
                            <AlertTriangle className="h-3 w-3" />
                            {nCaveats} valkuil{nCaveats === 1 ? "" : "en"}
                          </Badge>
                        )}
                      </span>
                    )}
                  </span>
                  <span className="absolute right-4 top-3 text-muted-foreground md:hidden" aria-hidden>{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
                  <span role="cell" className="font-mono text-xs text-muted-foreground md:pt-0.5">
                    <span className="md:hidden">{[colType(c), roleLabel(str(c.role))].filter(Boolean).join(" · ")}</span>
                    <span className="hidden md:inline">{colType(c) ?? "–"}</span>
                  </span>
                  <span role="cell" className="hidden text-[13px] md:block md:pt-0.5">
                    {roleLabel(str(c.role))}
                  </span>
                  <span role="cell" className={cn("min-w-0 text-[13px] leading-relaxed text-foreground", !isOpen && "line-clamp-2")}>
                    {meaning ? <InlineText text={meaning} /> : <span className="text-muted-foreground">Geen betekenis vastgelegd</span>}
                  </span>
                  <span role="cell" className="flex items-center gap-2 md:justify-end md:pt-1">
                    {jobs !== undefined ? (
                      <span className="text-[13px] tabular-nums text-muted-foreground">
                        {jobs.toLocaleString("nl-NL")}
                        <span className="md:hidden"> queries (180 d)</span>
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">–</span>
                    )}
                  </span>
                  <span className="hidden pt-0.5 text-muted-foreground md:block">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
                </button>
                {isOpen && (
                  <ColumnDetail
                    c={c}
                    onOpenTable={onOpenTable}
                    onNext={
                      idx < shown.length - 1
                        ? () => {
                            const n = shown[idx + 1];
                            const nk = `${n.i}:${colName(n.c)}`;
                            setOpen(new Set([nk]));
                            setTimeout(() => document.getElementById(`kol-${nk}`)?.scrollIntoView({ block: "nearest" }), 0);
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Joins, recipes, usage, provenance
// ---------------------------------------------------------------------------

const CONF_LABEL: Record<string, string> = { high: "hoog", medium: "middel", low: "laag" };
const JOIN_TYPE: Record<string, string> = { foreign_key: "Foreign key", observed: "Waargenomen", curated: "Gecureerd", lineage: "Lineage" };

function JoinsList({ joins, observed, checks, onOpenTable }: { joins: unknown[]; observed?: unknown; checks?: unknown; onOpenTable?: (fqn: string) => void }) {
  const ver = Array.isArray(checks) ? (checks.filter(isObj) as Obj[]) : [];
  /** Result of the verification check for this join, if one ran. */
  const validated = (j: Obj): boolean | null => {
    const c = ver.find((v) => String(v.claim ?? "").startsWith(`${str(j.column) ?? "?"} joins`) && String(v.claim).includes(shortFqn(str(j.to) ?? "?")));
    return c && typeof c.passed === "boolean" ? c.passed : null;
  };
  if (!joins.every(isObj)) return <ValueView value={joins} />;
  const obs = Array.isArray(observed) ? (observed.filter(isObj) as Obj[]) : [];
  const seen = (to?: string) => (to ? obs.filter((o) => str(o.with) && shortFqn(o.with as string) === shortFqn(to)) : []);
  const rows = (joins as Obj[]).map((j) => {
    const to = str(j.to) ?? str(j.table) ?? str(j.target);
    const pair = seen(to);
    const col = str(j.column);
    // Same predicate: the observed ON clause uses this table's join column.
    const exact = col ? pair.filter((o) => new RegExp(`\\.${col}\\b`).test(String(o.on ?? ""))) : [];
    const sum = (l: Obj[]) => l.reduce((a, o) => a + (num(o.jobs) ?? 0), 0);
    return { j, to, jobs: sum(exact), pairJobs: sum(pair), hits: exact };
  });
  rows.sort((a, b) => b.jobs - a.jobs);
  return (
    <div className="space-y-3">
      <p className="max-w-[80ch] text-sm text-muted-foreground">
        Drie soorten bewijs, apart getoond: <span className="font-medium text-foreground">afgeleid</span> (uit het schema),{" "}
        <span className="font-medium text-foreground">waargenomen</span> (analisten gebruikten deze voorwaarde de afgelopen 180 dagen) en{" "}
        <span className="font-medium text-foreground">gevalideerd</span> (een controle tegen de data is geslaagd). Sleutel-joins (…_dwhid) horen geen datumlogica in de
        ON-clausule te hebben.
      </p>
      <div className="rounded-md border border-border" role="table" aria-label="Joins">
        <div role="row" className="hidden grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1.3fr)_10rem] gap-4 border-b border-border bg-sl-grey-50 px-4 py-2 text-xs font-semibold text-muted-foreground lg:grid">
          <span role="columnheader">Sleutel</span>
          <span role="columnheader">Naar tabel</span>
          <span role="columnheader">Join-voorwaarde</span>
          <span role="columnheader">Bewijs</span>
        </div>
        {rows.map(({ j, to, jobs, pairJobs, hits }, i) => {
          const on = str(j.on) ?? str(j.via) ?? str(j.key);
          const conf = str(j.confidence);
          const rest = Object.fromEntries(Object.entries(j).filter(([k, v]) => !["to", "table", "target", "on", "via", "key", "confidence", "type", "column", "to_description"].includes(k) && !isEmpty(v)));
          const kinds = hits.map((h) => (isObj(h.kinds) ? Object.entries(h.kinds).map(([k, v]) => `${k} ${v}`).join(", ") : "")).filter(Boolean);
          return (
            <div key={i} role="row" className="grid gap-x-4 gap-y-1.5 border-b border-border px-4 py-3 last:border-0 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1.3fr)_10rem]">
              <span role="cell" className="min-w-0 break-all font-mono text-[13px] font-semibold">
                {str(j.column) ?? "–"}
              </span>
              <div role="cell" className="min-w-0">
                {to ? <TableLink fqn={to} onOpen={onOpenTable} /> : <span className="text-muted-foreground">onbekend</span>}
                {str(j.to_description) && <p className="mt-0.5 line-clamp-2 text-[13px] text-muted-foreground">{j.to_description as string}</p>}
              </div>
              <div role="cell" className="min-w-0">
                {on && <code className="block whitespace-pre-wrap break-all rounded bg-sl-grey-50 px-2 py-1 font-mono text-[13px]">{on}</code>}
              </div>
              <div role="cell" className="text-[13px]">
                <span className="block font-medium text-foreground">
                  {jobs > 0 ? `Waargenomen: ${jobs.toLocaleString("nl-NL")} queries` : pairJobs > 0 ? "Afgeleid" : "Afgeleid, niet waargenomen"}
                </span>
                {jobs === 0 && pairJobs > 0 && (
                  <span className="block text-xs text-muted-foreground">tabellen wel samen gebruikt ({pairJobs.toLocaleString("nl-NL")} queries), via een andere sleutel</span>
                )}
                {validated(j) === true && <span className="block text-xs font-medium text-success">gevalideerd</span>}
                {validated(j) === false && <span className="block text-xs font-medium text-destructive">validatie mislukt</span>}
                <span className="block text-xs text-muted-foreground">
                  {[str(j.type) && (JOIN_TYPE[j.type as string] ?? j.type), conf && `zekerheid ${CONF_LABEL[conf] ?? conf}`, ...kinds].filter(Boolean).join(" · ")}
                </span>
              </div>
              {Object.keys(rest).length > 0 && (
                <div className="lg:col-span-4">
                  <ValueView value={rest} depth={1} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VerifiedBadge({ status }: { status?: string }) {
  if (!status) return null;
  if (status === "verified")
    return (
      <Badge tone="success">
        <CheckCircle2 className="h-3 w-3" />
        Geverifieerd
      </Badge>
    );
  if (status === "failed" || status === "error")
    return (
      <Badge tone="danger">
        <XCircle className="h-3 w-3" />
        Mislukt
      </Badge>
    );
  return <Badge tone="neutral">{status}</Badge>;
}

const PATTERN_LABEL: Record<string, string> = { join: "Join", date_filter: "Datumfilter", aggregation: "Aggregatie", filter: "Filter", window: "Window", lookup: "Opzoeken" };

function Recipes({ recipes }: { recipes: unknown[] }) {
  return (
    <div className="space-y-4">
      {recipes.map((r, i) => {
        if (typeof r === "string") return looksLikeSql(r) ? <CodeBlock key={i} code={r} /> : <p key={i}>{r}</p>;
        if (!isObj(r)) return <ValueView key={i} value={r} />;
        const { title, name, description, explanation, question, sql, query, pattern, verification, ...rest } = r;
        const heading = str(title) ?? str(name) ?? str(question) ?? `Recept ${i + 1}`;
        const code = str(sql) ?? str(query);
        const ver = isObj(verification) ? verification : null;
        const verBits = ver
          ? [num(ver.rows) !== undefined && `${num(ver.rows)!.toLocaleString("nl-NL")} rijen`, num(ver.bytes) !== undefined && `${formatBytes(num(ver.bytes))} gescand`].filter(Boolean)
          : [];
        const { status: _s, rows: _r, bytes: _b, ...verRest } = ver ?? {};
        return (
          <article key={i} className="rounded-lg border border-border bg-card">
            <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3">
              <h4 className="min-w-0 flex-1 text-[15px] font-semibold leading-snug">{heading}</h4>
              <div className="flex flex-wrap items-center gap-1.5">
                {str(pattern) && <Badge tone="neutral">{PATTERN_LABEL[pattern as string] ?? (pattern as string)}</Badge>}
                <VerifiedBadge status={str(ver?.status)} />
              </div>
            </div>
            {(str(explanation) ?? str(description)) && (
              <p className="max-w-[90ch] px-4 pt-1.5 text-sm leading-relaxed text-muted-foreground">
                <InlineText text={(str(explanation) ?? str(description))!} />
              </p>
            )}
            {verBits.length > 0 && <p className="px-4 pt-1.5 text-xs tabular-nums text-muted-foreground">Proefrun: {verBits.join(" · ")}</p>}
            <div className="space-y-3 p-4 pt-3">
              {code && <CodeBlock code={code} maxHeight={320} />}
              {!code && !isEmpty(sql ?? query) && <ValueView value={sql ?? query} />}
              {Object.keys(verRest).length > 0 && (
                <details className="group rounded-md border border-border">
                  <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">Resultaat van de proefrun</summary>
                  <div className="border-t border-border p-3 text-xs">
                    <ValueView value={verRest} depth={1} />
                  </div>
                </details>
              )}
              {Object.keys(rest).length > 0 && <ValueView value={rest} depth={1} />}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function Bars({ rows, unit = "queries" }: { rows: [string, number][]; unit?: string }) {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[minmax(0,14rem)_1fr_4.5rem] items-center gap-3 text-sm">
          <span className="truncate font-mono text-[13px]" title={k}>
            {k}
          </span>
          <span className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
            <span className="block h-full rounded-full bg-sl-charcoal/70" style={{ width: `${Math.max(2, (v / max) * 100)}%` }} />
          </span>
          <span className="text-right text-xs tabular-nums text-muted-foreground" title={`${v} ${unit}`}>
            {v.toLocaleString("nl-NL")}
          </span>
        </div>
      ))}
    </div>
  );
}

const CLIENT_LABEL: Record<string, string> = { console_or_other: "Console / overig", tableau: "Tableau", looker: "Looker", python: "Python", dbt: "dbt" };

function Usage({ u, onOpenTable }: { u: Obj; onOpenTable?: (fqn: string) => void }) {
  const known = ["source", "jobs", "users", "last_used", "popularity", "rank", "of_tables", "by_client", "by_principal_type", "top_columns", "filters", "joins", "templates", "aggregations"];
  const rest = Object.fromEntries(Object.entries(u).filter(([k, v]) => !known.includes(k) && !isEmpty(v)));
  const topCols = Array.isArray(u.top_columns) ? (u.top_columns.filter(isObj) as Obj[]) : [];
  const byClient = isObj(u.by_client) ? (Object.entries(u.by_client).filter(([, v]) => typeof v === "number") as [string, number][]) : [];
  const filters = Array.isArray(u.filters) ? (u.filters.filter(isObj) as Obj[]) : [];
  const joins = Array.isArray(u.joins) ? (u.joins.filter(isObj) as Obj[]) : [];
  const templates = Array.isArray(u.templates) ? (u.templates.filter(isObj) as Obj[]) : [];
  const aggs = Array.isArray(u.aggregations) ? (u.aggregations.filter(isObj) as Obj[]) : [];
  return (
    <div className="space-y-8">
      <dl className="grid grid-cols-2 gap-4 rounded-lg border border-border p-4 sm:grid-cols-4">
        <Fact label="Queries">{num(u.jobs)?.toLocaleString("nl-NL") ?? "–"}</Fact>
        <Fact label="Gebruikers">{num(u.users)?.toLocaleString("nl-NL") ?? "–"}</Fact>
        <Fact label="Laatst gebruikt">{formatDate(str(u.last_used))}</Fact>
        <Fact label="Populariteit">{num(u.rank) !== undefined ? `#${u.rank} van ${num(u.of_tables) ?? "?"}` : "–"}</Fact>
      </dl>
      {str(u.source) && <p className="-mt-6 text-xs text-muted-foreground">Bron: {u.source as string}</p>}
      <div className="grid gap-8 xl:grid-cols-2">
        {topCols.length > 0 && (
          <Block title="Meest gebruikte kolommen" icon={<Columns3 className="h-3.5 w-3.5" />}>
            <Bars rows={topCols.slice(0, 12).map((c) => [String(c.column ?? "?"), num(c.jobs) ?? 0])} />
          </Block>
        )}
        {byClient.length > 0 && (
          <Block title="Per tool" icon={<BarChart3 className="h-3.5 w-3.5" />}>
            <Bars rows={byClient.map(([k, v]) => [CLIENT_LABEL[k] ?? k, v])} />
          </Block>
        )}
        {filters.length > 0 && (
          <Block title="Veelgebruikte filters">
            <Bars rows={filters.slice(0, 10).map((f) => [String(f.pattern ?? "?"), num(f.jobs) ?? 0])} />
          </Block>
        )}
        {aggs.length > 0 && (
          <Block title="Veelgebruikte aggregaties">
            <Bars rows={aggs.slice(0, 10).map((f) => [String(f.expr ?? "?"), num(f.jobs) ?? 0])} />
          </Block>
        )}
      </div>
      {joins.length > 0 && (
        <Block title="Waargenomen joins" icon={<Link2 className="h-3.5 w-3.5" />}>
          <div className="divide-y divide-border rounded-md border border-border">
            {joins.map((j, i) => {
              const { with: w, on, jobs, kinds, ...jr } = j;
              return (
                <div key={i} className="grid gap-x-4 gap-y-1 px-4 py-2.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_7rem]">
                  <div className="min-w-0">{str(w) ? <TableLink fqn={w as string} onOpen={onOpenTable} /> : "–"}</div>
                  <code className="min-w-0 whitespace-pre-wrap break-all font-mono text-xs text-muted-foreground">{str(on) ?? ""}</code>
                  <span className="text-xs tabular-nums text-muted-foreground lg:text-right">
                    {num(jobs)?.toLocaleString("nl-NL") ?? "–"} queries
                    {isObj(kinds) && <span className="block">{Object.entries(kinds).map(([k, v]) => `${k} ${v}`).join(" · ")}</span>}
                  </span>
                  {Object.keys(jr).filter((k) => !isEmpty(jr[k])).length > 0 && (
                    <div className="text-xs text-muted-foreground lg:col-span-3">
                      {Object.entries(jr)
                        .filter(([, v]) => !isEmpty(v))
                        .map(([k, v]) => `${humanizeKey(k)}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
                        .join(" · ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Block>
      )}
      {templates.length > 0 && (
        <Block title="Veelvoorkomende querypatronen" icon={<FileCode2 className="h-3.5 w-3.5" />}>
          <div className="space-y-3">
            {templates.slice(0, 8).map((t, i) => (
              <CodeBlock
                key={i}
                code={String(t.sql ?? "")}
                maxHeight={200}
                title={
                  <span>
                    {num(t.jobs) ?? "?"} queries · {num(t.users) ?? "?"} gebruikers
                  </span>
                }
              />
            ))}
          </div>
        </Block>
      )}
      {Object.keys(rest).length > 0 && (
        <Block title="Overig gebruik">
          <ValueView value={rest} />
        </Block>
      )}
    </div>
  );
}

function VerificationList({ items }: { items: unknown[] }) {
  if (!items.every(isObj)) return <ValueView value={items} />;
  return (
    <div className="divide-y divide-border rounded-md border border-border">
      {(items as Obj[]).map((v, i) => {
        const st = checkState(v);
        const { claim, check, passed: _p, result, ...rest } = v;
        const resObj = isObj(result) ? result : null;
        return (
          <div key={i} className="flex gap-3 px-4 py-2.5">
            <span className="mt-0.5 shrink-0">
              {st.kind === "passed" ? (
                <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
              ) : st.kind === "failed" ? (
                <XCircle className="h-4 w-4 text-destructive" aria-hidden />
              ) : (
                <CircleDashed className="h-4 w-4 text-muted-foreground" aria-hidden />
              )}
            </span>
            <div className="min-w-0 flex-1 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{str(claim) ?? "Controle"}</span>
                <span className={cn("text-[13px] font-semibold", st.kind === "passed" ? "text-success" : st.kind === "failed" ? "text-destructive" : "text-muted-foreground")}>{st.label}</span>
              </div>
              {resObj && (
                <p className="mt-0.5 text-[13px] text-muted-foreground">
                  {Object.entries(resObj)
                    .map(([k, x]) => `${humanizeKey(k).toLowerCase()} ${typeof x === "number" ? x.toLocaleString("nl-NL") : String(x)}`)
                    .join(" · ")}
                </p>
              )}
              {typeof result === "string" && result && (
                <details className="mt-0.5 text-[13px] text-muted-foreground">
                  <summary className="cursor-pointer select-none hover:text-foreground">Foutmelding tonen</summary>
                  <p className="mt-1 break-all font-mono text-xs">{result}</p>
                </details>
              )}
              {(str(check) || Object.keys(rest).length > 0) && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {str(check) && `soort: ${check}`}
                  {Object.keys(rest).length > 0 && ` · ${JSON.stringify(rest)}`}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type CheckKind = "passed" | "failed" | "budget" | "error" | "notrun";
/** A check either ran (passed/failed) or could not complete; for the latter, say why in plain words. */
function checkState(v: Obj): { kind: CheckKind; label: string } {
  if (v.passed === true) return { kind: "passed", label: "geslaagd" };
  if (v.passed === false) return { kind: "failed", label: "mislukt: bewering klopt niet" };
  const r = String(v.result ?? "");
  if (/bytes billed|bytesBilledLimitExceeded/i.test(r)) return { kind: "budget", label: "niet afgerond: query te groot voor het controlebudget" };
  if (r) return { kind: "error", label: "niet afgerond: fout in de controlequery" };
  return { kind: "notrun", label: "niet uitgevoerd" };
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export type CardSection = "overzicht" | "kolommen" | "joins" | "recepten" | "gebruik" | "herkomst";

const OVERVIEW_KEYS = [
  "description",
  "purpose",
  "summary",
  "grain",
  "business_process",
  "usage_guidance",
  "caveats",
  "pitfalls",
  "limitations",
  "coverage",
  "synonyms",
  "glossary_terms",
  "kpis",
  "related_reporting_models",
  "referenced_by",
  "tenant_copies_excluded",
];
const HEADER_KEYS = [
  "id",
  "fqn",
  "table",
  "dataset",
  "name",
  "kind",
  "type",
  "layer",
  "domain",
  "quality",
  "rows",
  "size_bytes",
  "last_modified",
  "partitioned_by",
  "clustered_by",
  "primary_key",
  "primary_key_stated",
  "business_key",
  "main_date_column",
  "confidence",
];
const PROVENANCE_KEYS = ["verification", "sources", "sql_source", "models", "enrichment", "built_at", "profiled_at", "sampled_pct", "profile_bytes", "profile_skipped", "fingerprint", "scd2", "date_columns"];
const SECTION_KEYS = ["columns", "joins", "recipes", "observed_usage"];
const CONSUMED = new Set([...OVERVIEW_KEYS, ...HEADER_KEYS, ...PROVENANCE_KEYS, ...SECTION_KEYS]);

export function cardSections(card: TableCard): { id: CardSection; label: string; count?: number }[] {
  const len = (v: unknown) => (Array.isArray(v) ? v.length : undefined);
  const s: { id: CardSection; label: string; count?: number }[] = [{ id: "overzicht", label: "Overzicht" }];
  if (!isEmpty(card.columns)) s.push({ id: "kolommen", label: "Kolommen", count: len(card.columns) });
  if (!isEmpty(card.joins)) s.push({ id: "joins", label: "Joins", count: len(card.joins) });
  if (!isEmpty(card.recipes)) s.push({ id: "recepten", label: "Recepten", count: len(card.recipes) });
  if (isObj(card.observed_usage) && !isEmpty(card.observed_usage)) s.push({ id: "gebruik", label: "Gebruik" });
  s.push({ id: "herkomst", label: "Herkomst" });
  return s;
}

function GrainBlock({ grain }: { grain: unknown }) {
  if (typeof grain === "string") return <Prose value={grain} />;
  if (!isObj(grain)) return <ValueView value={grain} />;
  const { description, verified_key, checks, method, ...rest } = grain;
  const vk = strList(verified_key);
  const cs = Array.isArray(checks) ? (checks.filter(isObj) as Obj[]) : [];
  return (
    <div className="space-y-2">
      {str(description) ? <InlineText text={description as string} className="block text-[15px] leading-relaxed" /> : <p className="text-muted-foreground">Geen omschrijving.</p>}
      {vk.length ? (
        <p className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-success">
          <CheckCircle2 className="h-4 w-4" />
          Geverifieerd: <span className="font-mono">{vk.join(" + ")}</span> is uniek
          {str(method) && <span className="font-normal text-muted-foreground">({method as string})</span>}
        </p>
      ) : (
        <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <CircleDashed className="h-4 w-4" />
          Niet geverifieerd tegen de data
        </p>
      )}
      {cs.length > 0 && (
        <details className="rounded-md border border-border">
          <summary className="cursor-pointer select-none px-3 py-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground">Sleutelcontroles ({cs.length})</summary>
          <table className="w-full border-t border-border text-[13px]">
            <thead className="bg-sl-grey-50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 font-semibold">Kolommen</th>
                <th className="px-3 py-1.5 font-semibold">Uniek</th>
                <th className="px-3 py-1.5 text-right font-semibold">Rijen per sleutel</th>
              </tr>
            </thead>
            <tbody>
              {cs.map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono">{strList(c.columns).join(" + ") || "–"}</td>
                  <td className="px-3 py-1.5">{c.unique === true ? "ja" : c.unique === false ? "nee" : "–"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(c.rows_per_key)?.toLocaleString("nl-NL") ?? "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {!isEmpty(rest) && <ValueView value={rest} depth={1} />}
    </div>
  );
}

/** Pass/fail/not-run counts of the card's verification checks. */
export function verificationSummary(card: TableCard) {
  const v = Array.isArray(card.verification) ? (card.verification.filter(isObj) as Obj[]) : [];
  return { total: v.length, passed: v.filter((x) => x.passed === true).length, failed: v.filter((x) => x.passed === false).length, notRun: v.filter((x) => x.passed !== true && x.passed !== false).length };
}

function VerificationBadge({ card, onClick }: { card: TableCard; onClick?: () => void }) {
  const s = verificationSummary(card);
  if (!s.total) return null;
  const tone = s.failed ? "danger" : s.passed === s.total ? "success" : "neutral";
  const parts = [`${s.passed} geslaagd`, s.failed ? `${s.failed} mislukt` : null, s.notRun ? `${s.notRun} niet afgerond` : null].filter(Boolean);
  return (
    <button type="button" onClick={onClick} title="Controles van deze kaart tegen de data. Klik voor details." className="rounded-full">
      <Badge tone={tone}>
        {s.failed ? <XCircle className="h-3 w-3" /> : s.passed === s.total ? <CheckCircle2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />}
        Controles: {parts.join(" · ")}
      </Badge>
    </button>
  );
}

function SynonymsBlock({ value }: { value: unknown }) {
  if (isObj(value)) {
    return (
      <div className="space-y-2">
        {Object.entries(value).map(([lang, v]) => (
          <div key={lang} className="flex gap-3">
            <span className="w-7 shrink-0 pt-0.5 text-xs font-semibold uppercase text-muted-foreground">{lang}</span>
            <Chips items={strList(v)} />
          </div>
        ))}
      </div>
    );
  }
  return <Chips items={strList(value)} />;
}

function Overview({ card, onOpenTable, onSection }: { card: TableCard; onOpenTable?: (fqn: string) => void; onSection?: (s: CardSection) => void }) {
  const caveats = [card.caveats, card.pitfalls].flatMap((v) => (Array.isArray(v) ? v : v ? [v] : []));
  const related = [...strList(card.related_reporting_models), ...strList(card.referenced_by)];
  const pk = strList(card.primary_key);
  const bk = strList(card.business_key);
  const joins = Array.isArray(card.joins) ? (card.joins.filter(isObj) as Obj[]) : [];
  const guidance = typeof card.usage_guidance === "string" ? splitRules(card.usage_guidance) : null;
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className="max-w-[1040px] space-y-8">
      {!isEmpty(card.purpose) && (
        <div>
          <Prose value={card.purpose} />
        </div>
      )}
      <div className="min-w-0 space-y-8">
        {/* Key facts for writing SQL, before any prose. */}
        <div className="divide-y divide-border rounded-lg border border-border">
          {!isEmpty(card.grain) && (
            <KeyRow label="Eén rij is">
              <GrainBlock grain={card.grain} />
            </KeyRow>
          )}
          {(pk.length > 0 || bk.length > 0) && (
            <KeyRow label="Sleutels">
              <div className="space-y-0.5 text-[15px]">
                {pk.length > 0 && (
                  <div>
                    <span className="font-mono text-sm font-semibold">{pk.join(", ")}</span> <span className="text-[13px] text-muted-foreground">primaire sleutel (DWHID)</span>
                  </div>
                )}
                {bk.length > 0 && (
                  <div>
                    <span className="font-mono text-sm">{bk.join(" + ")}</span> <span className="text-[13px] text-muted-foreground">business key</span>
                  </div>
                )}
              </div>
            </KeyRow>
          )}
          {joins.length > 0 && (
            <KeyRow label="Joinen via">
              <ul className="space-y-1">
                {joins.slice(0, 4).map((j, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="font-mono text-[13px] font-semibold">{str(j.column) ?? "?"}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-label="naar" />
                    {str(j.to) ? <TableLink fqn={j.to as string} onOpen={onOpenTable} /> : "–"}
                  </li>
                ))}
              </ul>
              {onSection && (
                <button type="button" onClick={() => onSection("joins")} className="mt-1.5 text-[13px] font-medium text-info hover:underline">
                  Alle {joins.length} joins met voorwaarden en bewijs
                </button>
              )}
            </KeyRow>
          )}
          {caveats.length > 0 && (
            <KeyRow label="Let op">
              <ul className="space-y-1.5">
                {caveats.slice(0, 3).map((c, i) => (
                  <li key={i} className="flex gap-2 text-sm leading-relaxed">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sl-yellow-dark" aria-hidden />
                    <InlineText text={caveatText(c).text} />
                  </li>
                ))}
              </ul>
              {caveats.length > 3 && (
                <button type="button" onClick={() => jump("kaart-valkuilen")} className="mt-1.5 text-[13px] font-medium text-info hover:underline">
                  Alle {caveats.length} valkuilen
                </button>
              )}
            </KeyRow>
          )}
        </div>

        {!isEmpty(card.usage_guidance) && (
          <Block title="Gebruiksadvies voor SQL">
            {guidance && guidance.length > 1 ? (
              <ul className="max-w-[75ch] list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed marker:text-muted-foreground">
                {guidance.map((g, i) => (
                  <li key={i}>
                    <InlineText text={g} />
                  </li>
                ))}
              </ul>
            ) : (
              <Prose value={card.usage_guidance} />
            )}
          </Block>
        )}
        {!isEmpty(card.business_process) && (
          <Block title="Bedrijfsproces">
            <Prose value={card.business_process} />
          </Block>
        )}
        {caveats.length > 0 && (
          <div id="kaart-valkuilen" className="scroll-mt-16">
            <Block title={`Valkuilen (${caveats.length})`} icon={<AlertTriangle className="h-3.5 w-3.5" />}>
              <CaveatList items={caveats} />
            </Block>
          </div>
        )}
        {!isEmpty(card.limitations) && (
          <Block title="Beperkingen">
            <ValueView value={card.limitations} />
          </Block>
        )}
        {!isEmpty(card.coverage) && (
          <Block title="Dekking">
            <ValueView value={card.coverage} />
          </Block>
        )}
      </div>
      <div className="grid gap-x-8 gap-y-6 border-t border-border pt-6 md:grid-cols-2">
        <h3 className="text-sm font-semibold md:col-span-2">Meer over deze tabel</h3>
        {!isEmpty(card.description) && (
          <details className="md:col-span-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-muted-foreground hover:text-foreground">Oorspronkelijke beschrijving uit dbt (bron, Nederlands)</summary>
            <InlineText text={typeof card.description === "string" ? card.description : JSON.stringify(card.description)} className="mt-2 block max-w-[75ch] text-sm leading-relaxed" />
          </details>
        )}
        {!isEmpty(card.summary) && typeof card.summary !== "string" && (
          <Block title="Samenvatting">
            <ValueView value={card.summary} />
          </Block>
        )}
        {!isEmpty(card.synonyms) && (
          <Block title="Synoniemen">
            <SynonymsBlock value={card.synonyms} />
          </Block>
        )}
        {related.length > 0 && (
          <Block title="Gerelateerde modellen" icon={<Link2 className="h-3.5 w-3.5" />}>
            <ul className="space-y-1">
              {related.map((r) => (
                <li key={r}>
                  <TableLink fqn={r} onOpen={onOpenTable} />
                </li>
              ))}
            </ul>
          </Block>
        )}
        {strList(card.tenant_copies_excluded).length > 0 && (
          <Block title="Uitgesloten tenant-kopieën">
            <Chips items={strList(card.tenant_copies_excluded)} mono />
          </Block>
        )}
        {!isEmpty(card.glossary_terms) && (
          <Block title="Begrippen">
            <ValueView value={card.glossary_terms} />
          </Block>
        )}
        {!isEmpty(card.kpis) && (
          <Block title="KPI's">
            <ValueView value={card.kpis} />
          </Block>
        )}
      </div>
    </div>
  );
}

function KeyRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 px-4 py-3 sm:grid-cols-[7.5rem_1fr] sm:gap-4">
      <div className="pt-0.5 text-[13px] font-semibold text-muted-foreground">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Split guidance prose into sentence-level rules (keeps `code` spans intact). */
function splitRules(s: string): string[] {
  const out: string[] = [];
  let buf = "";
  let inCode = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    buf += ch;
    if (ch === "`") inCode = !inCode;
    if (!inCode && (ch === "." || ch === ";") && s[i + 1] === " " && /[A-Z`(]/.test(s[i + 2] ?? "")) {
      out.push(buf.trim());
      buf = "";
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

function Provenance({ card }: { card: TableCard }) {
  const rest = Object.entries(card).filter(([k, v]) => !CONSUMED.has(k) && !isEmpty(v));
  const ver = Array.isArray(card.verification) ? card.verification : null;
  const scd2 = isObj(card.scd2) ? card.scd2 : null;
  const meta: [string, ReactNode][] = [];
  if (num(card.confidence) !== undefined)
    meta.push(["Geschatte zekerheid", `${Math.round(num(card.confidence)! * 100)}% — inschatting van het bouwproces (AI), geen testresultaat. Zie de controles hierboven voor wat echt getest is.`]);
  if (str(card.built_at)) meta.push(["Kaart gebouwd", formatDateTime(card.built_at as string)]);
  if (str(card.profiled_at)) meta.push(["Geprofileerd", `${formatDateTime(card.profiled_at as string)}${num(card.sampled_pct) !== undefined ? ` · steekproef ${card.sampled_pct}%` : ""}`]);
  if (isObj(card.models)) meta.push(["Modellen", Object.entries(card.models).map(([k, v]) => `${humanizeKey(k).toLowerCase()}: ${v}`).join(" · ")]);
  if (isObj(card.enrichment))
    meta.push([
      "Verrijking",
      `${card.enrichment.complete === true ? "volledig" : "onvolledig"}${str(card.enrichment.enriched_at) ? ` · ${formatDateTime(card.enrichment.enriched_at as string)}` : ""}${num(card.enrichment.seconds) !== undefined ? ` · ${card.enrichment.seconds} s` : ""}`,
    ]);
  if (str(card.sql_source)) meta.push(["SQL-bron", card.sql_source as string]);
  if (scd2) meta.push(["SCD type 2", scd2.is_scd2 ? `ja (${[scd2.valid_from, scd2.valid_to, scd2.current_flag].filter(Boolean).join(", ")})` : "nee"]);
  if (!isEmpty(card.date_columns)) meta.push(["Datumkolommen", <ValueView value={card.date_columns} depth={2} />]);
  if (num(card.profile_bytes) !== undefined) meta.push(["Profielkosten", formatBytes(num(card.profile_bytes))]);
  if (isObj(card.fingerprint)) meta.push(["Vingerafdruk", <span className="font-mono text-xs">{Object.entries(card.fingerprint).map(([k, v]) => `${k}=${v}`).join(" · ")}</span>]);

  return (
    <div className="space-y-8">
      {ver && ver.length > 0 && (
        <Block title={`Controles (${ver.length})`} icon={<CheckCircle2 className="h-3.5 w-3.5" />}>
          {(() => {
            const states = (ver.filter(isObj) as Obj[]).map(checkState);
            const n = (k: CheckKind) => states.filter((x) => x.kind === k).length;
            const bits = [
              [n("passed"), "geslaagd", "text-success"],
              [n("failed"), "mislukt (bewering klopt niet)", "text-destructive"],
              [n("budget"), "niet afgerond door controlebudget", "text-muted-foreground"],
              [n("error"), "niet afgerond door een fout in de controlequery", "text-muted-foreground"],
              [n("notrun"), "niet uitgevoerd", "text-muted-foreground"],
            ].filter(([c]) => (c as number) > 0) as [number, string, string][];
            return (
              <p className="mb-2 text-sm">
                {bits.map(([c, l, cls], i) => (
                  <span key={l}>
                    {i > 0 && " · "}
                    <span className={cn("font-semibold", cls)}>{c}</span> {l}
                  </span>
                ))}
                . Niet-afgeronde controles zeggen niets over de juistheid van de kaart.
              </p>
            );
          })()}
          <VerificationList items={ver} />
        </Block>
      )}
      {meta.length > 0 && (
        <Block title="Opbouw van de kaart" icon={<Fingerprint className="h-3.5 w-3.5" />}>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[11rem_1fr]">
            {meta.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-xs font-medium text-muted-foreground sm:pt-0.5">{k}</dt>
                <dd className="min-w-0 break-words">{v}</dd>
              </div>
            ))}
          </dl>
        </Block>
      )}
      {isObj(card.sources) && (
        <Block title="Herkomst per veld">
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(card.sources).map(([k, v]) => (
              <span key={k} className="rounded-md border border-border px-2 py-0.5 text-xs">
                <span className="font-mono">{k}</span> <span className="text-muted-foreground">· {SOURCE_LABEL[String(v)] ?? String(v)}</span>
              </span>
            ))}
          </div>
        </Block>
      )}
      {strList(card.profile_skipped).length > 0 && (
        <Block title={`Overgeslagen profilering (${strList(card.profile_skipped).length})`}>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {strList(card.profile_skipped).map((s, i) => (
              <li key={i} className="line-clamp-2 break-all" title={s}>
                {s}
              </li>
            ))}
          </ul>
        </Block>
      )}
      {rest.map(([k, v]) => (
        <Block key={k} title={humanizeKey(k)}>
          <ValueView value={v} />
        </Block>
      ))}
      <details className="rounded-md border border-border">
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Ruwe kaart (JSON)</summary>
        <div className="border-t border-border p-3">
          <CodeBlock code={JSON.stringify(card, null, 2)} language="json" maxHeight={480} copyLabel="Kopieer JSON" />
        </div>
      </details>
    </div>
  );
}

/** Header of a table card: identity, key facts. */
export function CardHeaderBlock({ card, fqn, actions, onSection }: { card: TableCard; fqn: string; actions?: ReactNode; onSection?: (s: CardSection) => void }) {
  const fullName = str(card.table) ?? str(card.fqn) ?? fqn;
  const name = str(card.name) ?? fqn.split(".").pop() ?? fqn;
  const dataset = str(card.dataset) ?? (fqn.includes(".") ? fqn.split(".")[0] : undefined);
  const quality = isObj(card.quality) ? (card.quality as { tests_passed: number; tests_total: number }) : null;
  const partition = strList(card.partitioned_by);
  const usage = isObj(card.observed_usage) ? card.observed_usage : null;
  const facts: [ReactNode, string?][] = [];
  if (num(card.rows) !== undefined) facts.push([`${formatCount(num(card.rows)!)} rijen`, `${num(card.rows)!.toLocaleString("nl-NL")} rijen`]);
  if (num(card.size_bytes) !== undefined) facts.push([formatBytes(num(card.size_bytes))]);
  facts.push([
    partition.length ? (
      <>
        partitie <span className="font-mono text-foreground">{partition.join(", ")}</span>
      </>
    ) : (
      "geen partitie"
    ),
  ]);
  if (str(card.last_modified)) facts.push([`bijgewerkt ${formatDate(card.last_modified as string)}`, formatDateTime(card.last_modified as string)]);
  if (usage && num(usage.jobs) !== undefined)
    facts.push([`${formatCount(num(usage.jobs)!)} queries in 180 d`, `${num(usage.jobs)} queries door ${num(usage.users) ?? "?"} gebruikers${num(usage.rank) ? ` · #${usage.rank} van ${usage.of_tables} tabellen` : ""}`]);

  return (
    <div className="px-4 pb-3 pt-3 sm:px-5 sm:pt-4 lg:px-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <h2 className="min-w-0 font-mono text-xl font-semibold leading-tight text-foreground [overflow-wrap:anywhere] lg:text-[22px]">{name}</h2>
            {str(card.kind) && <KindBadge kind={card.kind as string} />}
            <VerificationBadge card={card} onClick={onSection ? () => onSection("herkomst") : undefined} />
            {quality && <QualityIndicator quality={quality} />}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
            <span className="inline-flex min-w-0 items-center gap-1">
              <span className="truncate font-mono" title={fullName}>
                {dataset ?? fullName}
              </span>
              <CopyButton text={fullName} label={`Kopieer ${fullName}`} iconOnly className="h-6 w-6 border-transparent" />
            </span>
            {facts.map(([f, t], i) => (
              <span key={i} title={t} className={cn("whitespace-nowrap", i >= 2 && "hidden sm:inline")}>
                <span className="mr-2 text-border" aria-hidden>
                  |
                </span>
                {f}
              </span>
            ))}
          </div>
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function CardSectionBody({ card, section, onOpenTable, onSection }: { card: TableCard; section: CardSection; onOpenTable?: (fqn: string) => void; onSection?: (s: CardSection) => void }) {
  const columns = Array.isArray(card.columns) && card.columns.every(isObj) ? (card.columns as Obj[]) : null;
  switch (section) {
    case "kolommen":
      return columns ? (
        <ColumnsTable columns={columns} pk={strList(card.primary_key)} partition={strList(card.partitioned_by)} onOpenTable={onOpenTable} />
      ) : (
        <ValueView value={card.columns} />
      );
    case "joins":
      return Array.isArray(card.joins) ? <JoinsList joins={card.joins} observed={isObj(card.observed_usage) ? card.observed_usage.joins : undefined} checks={card.verification} onOpenTable={onOpenTable} /> : <ValueView value={card.joins} />;
    case "recepten":
      return <Recipes recipes={Array.isArray(card.recipes) ? card.recipes : [card.recipes]} />;
    case "gebruik":
      return isObj(card.observed_usage) ? <Usage u={card.observed_usage} onOpenTable={onOpenTable} /> : null;
    case "herkomst":
      return <Provenance card={card} />;
    default:
      return <Overview card={card} onOpenTable={onOpenTable} onSection={onSection} />;
  }
}

/** Convenience: whole card (header + overview) for places without tabs. */
export function ContextCard({ card, fqn }: { card: TableCard; fqn: string }) {
  return (
    <div>
      <CardHeaderBlock card={card} fqn={fqn} />
      <div className="px-5 pb-6 lg:px-8">
        <CardSectionBody card={card} section="overzicht" />
      </div>
    </div>
  );
}

